import { Email } from '@price-for-profit/micro-services';
import { CLIENT_ID } from 'shared/constants';
import {
    ApprovalTierChangeEmailData,
    ApprovedEmailData,
    NeedsApprovalEmailData,
    NeedsApprovalTierChangeEmailData,
    RejectionEmailData,
    SimpleEmailData,
} from 'types/backend';

export function buildEmail({
    subject,
    body,
    to,
    cc,
}: {
    subject: string;
    body: string;
    to: string[];
    cc: string[];
}): Email {
    return {
        actor: 'Paragon PMT Drive Application',
        clientId: CLIENT_ID,
        subject,
        body,
        to,
        cc,
        bcc: [],
    };
}

export function getDeviationNeedsApprovalEmailTemplate(
    {
        createdBy,
        customerName,
        customerAccount,
        endUserName,
        endUserAccount,
        customerTier,
        territorySalesLeader,
        customerTierTargetPrice,
        pricingGroup,
        isOneTimeOnly,
        productId,
        currentPrice,
        newPrice,
        variance,
        estAnnualVolume,
        deviationReason,
        comments,
        approverEmails,
    }: NeedsApprovalEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `Action Required - Deviation Approval - ${customerName}`,
        body: `
            Please review this deviation at the link below:
            ${url}
            
            Deviation Details
            Created by: ${createdBy}
            Distributor: ${customerName} | ${customerAccount}
            End User: ${endUserName} | ${endUserAccount}
            Pricing Group: ${pricingGroup ?? 'N/A'}
            OTO: ${isOneTimeOnly ? 'Y' : 'N'}
            Product: ${productId}
            Customer Tier: ${customerTier}

            Territory Sales Leader: ${territorySalesLeader}
            
            
            Deviated Price: ${newPrice}
            Customer Tier Target Price: ${customerTierTargetPrice}
            Current Price: ${currentPrice}
            Variance from Customer Tier Target Price: ${variance}
            
            
            Est. Annual Volume: ${estAnnualVolume}
            Deviation Reason: ${deviationReason}
            Requester Comments: ${comments}
            
            Thank you for your review.`,
        to: [...approverEmails],
        cc: ['ORDERS@PARAGONFILMS.COM'],
    });
}

export function getMarketPriceNeedsApprovalEmailTemplate(
    {
        createdBy,
        customerName,
        customerAccount,
        customerTier,
        territorySalesLeader,
        territorySalesRep,
        customerTierTargetPrice,
        tierOneTargetPrice,
        productId,
        currentPrice,
        newPrice,
        variance,
        estAnnualVolume,
        deviationReason,
        comments,
        approverEmails,
    }: NeedsApprovalEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `Action Required - Market Price  Approval - ${customerName}`,
        body: `
            Please review this market price request at the link below:
            ${url}
            
            Deviation Details
            Created by: ${createdBy}
            Distributor: ${customerName} | ${customerAccount}
            Product: ${productId}
            Customer Tier: ${customerTier}

            Territory Sales Leader: ${territorySalesLeader}
            Territory Sales Rep: ${territorySalesRep}
            
            
            Requested Market Price: ${newPrice}
            Customer Tier Target Price: ${customerTierTargetPrice}
            Tier 1 Product Target Price ${tierOneTargetPrice}
            Current Market Price: ${currentPrice}
            Variance from Customer Tier Target Price: ${variance}
            
            
            Est. Annual Volume: ${estAnnualVolume}
            Price Change Reason: ${deviationReason}
            Requester Comments: ${comments}
            
            Thank you for your review.`,
        to: [...approverEmails],
        cc: ['ORDERS@PARAGONFILMS.COM'],
    });
}

export function getSimpleNeedsApprovalEmailTemplate(
    { createdBy, customerName, productGroup, productId, approverEmails }: SimpleEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `Action Required - Deviation expired - ${customerName}`,
        body: `
            Please review this expired at the link below:
            ${url}
            
            Product Details
            Added by: ${createdBy}
            Distributor: ${customerName}
            Product: ${productGroup} | ${productId}
            
            Thank you for your review.`,
        to: [...approverEmails],
        cc: ['ORDERS@PARAGONFILMS.COM'],
    });
}
export function getDeviationRejectedEmailTemplate(
    {
        createdBy,
        customerName,
        endUserName,
        customerAccount,
        endUserAccount,
        customerTier,
        territorySalesLeader,
        territorySalesRep,
        customerTierTargetPrice,
        tierOneTargetPrice,
        pricingGroup,
        isOneTimeOnly,
        productId,
        currentPrice,
        suggestedPrice,
        newPrice,
        variance,
        comments,
        approverEmails,
        approverName,
    }: RejectionEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `[Not Approved] - Deviation for - ${customerName}`,
        body: `
           The following deviation was not approved, and requires further review:
            
            Deviation Details
            Created by: ${createdBy}
            Distributor: ${customerName} ${customerAccount}
            End User: ${endUserName} ${endUserAccount}
            Pricing Group: ${pricingGroup}
            OTO: ${isOneTimeOnly ? 'Y' : 'N'}
            Product: ${productId}
            Customer Tier: ${customerTier}

            Territory Sales Leader: ${territorySalesLeader}
            Territory Sales Rep: ${territorySalesRep}
            
            
            Deviated Price: ${newPrice}
            Customer Tier Target Price: ${customerTierTargetPrice}
            Tier 1 Product Target Price: ${tierOneTargetPrice}
            Current Market Price: ${currentPrice}
            Variance from Customer Tier Target Price: ${variance}
            
            Reviewed By: ${approverName}
            Suggested Price: ${suggestedPrice} 
            Reviewer Comments: ${comments}
            
            To view and update this deviation, click here: ${url}.`,
        to: [...approverEmails],
        cc: ['ORDERS@PARAGONFILMS.COM'],
    });
}

export function getMarketPriceRejectedEmailTemplate(
    {
        createdBy,
        customerName,
        endUserName,
        customerAccount,
        endUserAccount,
        customerTier,
        territorySalesLeader,
        territorySalesRep,
        customerTierTargetPrice,
        tierOneTargetPrice,
        pricingGroup,
        productId,
        currentPrice,
        suggestedPrice,
        newPrice,
        variance,
        comments,
        approverEmails,
        approverName,
    }: RejectionEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `[Not Approved] - Market Price for - ${customerName}`,
        body: `
           The following market price was not approved, and requires further review:
            
            Market Price Details
            Created by: ${createdBy}
            Distributor: ${customerName} | ${customerAccount}
            Product: ${productId}
            Customer Tier: ${customerTier}

            Territory Sales Leader: ${territorySalesLeader}
            Territory Sales Rep: ${territorySalesRep}
            
            
            Requested Market Price: ${newPrice}
            Customer Tier Target Price: ${customerTierTargetPrice}
            Tier 1 Product Target Price: ${tierOneTargetPrice}
            Current Market Price: ${currentPrice}
            Variance from Customer Tier Target Price: ${variance}
            
            Reviewed By: ${approverName}
            Suggested Price: ${suggestedPrice} 
            Reviewer Comments: ${comments}
            
            To view and update this market price, click here: ${url}.`,
        to: [...approverEmails],
        cc: ['ORDERS@PARAGONFILMS.COM'],
    });
}

export function getDeviationApprovedEmailTemplate(
    {
        createdBy,
        customerName,
        endUserName,
        customerAccount,
        endUserAccount,
        territorySalesLeader,
        territorySalesRep,
        pricingGroup,
        isOneTimeOnly,
        productGroup,
        productId,
        newPrice,
        approverName,
        requestorEmail,
    }: ApprovedEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `[Approved] - Deviation for - ${customerName}`,
        body: `
               The following deviation has been approved:
                
                Deviation Details
                Created by: ${createdBy}
                Distributor: ${customerName} ${customerAccount}
                End User: ${endUserName} ${endUserAccount}
                Pricing Group: ${pricingGroup}
                OTO: ${isOneTimeOnly ? 'Y' : 'N'}

                Product: ${productGroup} | ${productId}
    
                Territory Sales Leader: ${territorySalesLeader}
                Territory Sales Rep: ${territorySalesRep}
                
                
                Deviated Price: ${newPrice}
                Approved By: ${approverName}

            
                To view and update this deviation, click here: ${url}.`,
        to: [...requestorEmail],
        cc: ['ORDERS@PARAGONFILMS.COM'],
    });
}

export function getMarketPriceApprovedEmailTemplate(
    {
        createdBy,
        customerName,
        endUserName,
        customerAccount,
        endUserAccount,
        territorySalesLeader,
        territorySalesRep,
        pricingGroup,
        productGroup,
        productId,
        newPrice,
        approverName,
        requestorEmail,
    }: ApprovedEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `[Approved] - Market Price Change for - ${customerName}`,
        body: `
               The following market price has been approved:
                
                Market Price Details
                Created by: ${createdBy}
                Distributor: ${customerName} | ${customerAccount}
                Product: ${productId}
    
                Territory Sales Leader: ${territorySalesLeader}
                Territory Sales Rep: ${territorySalesRep}
                
                
                New Market Price: ${newPrice}
                Approved By: ${approverName}

            
                To view this market price, click here: ${url}.`,
        to: [...requestorEmail],
        cc: ['ORDERS@PARAGONFILMS.COM'],
    });
}
export function getTierChangeRequestEmailTemplate(
    {
        customerName,
        approver,
        createdBy,
        customerAccount,
        territorySalesLeader,
        territorySalesRep,
        revisedTier,
        currentTier,
        estAnnualVolume,
        tierRevisionReason,
        requestorComments,
    }: NeedsApprovalTierChangeEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `[Action Required] - Tier Change Approval - ${customerName}`,
        body: `
                   Please review this tier change request at the link below:
                   ${url}
                    
                    Tier Change Details
                    Approver: ${approver}
                    Created by: ${createdBy}
                    Distributor: ${customerName} | ${customerAccount}
        
                    Territory Sales Leader: ${territorySalesLeader}
                    Territory Sales Rep: ${territorySalesRep ?? 'N/A'}

                    Customer Revised Tier Requested: ${revisedTier}
                    Customer Current Tier: ${currentTier}

                    Est. Annual Volume: ${estAnnualVolume}
                    Tier Revision Reason: ${tierRevisionReason}
                    Requestor Comments: ${requestorComments}
                    
                    Thank you for your review.`,
        // to: ['clay.tiedeman@paragonfilms.com'],
        to: ['lfin.upstart@insight2profit.com'],
        // to: ['dmendoza@insight2profit.com'],
        cc: [],
    });
}

export function getTierChangeApprovedEmailTemplate(
    {
        customerName,
        approver,
        createdBy,
        customerAccount,
        territorySalesLeader,
        territorySalesRep,
        revisedTier,
        currentTier,
        estAnnualVolume,
        tierRevisionReason,
        requestorComments,
        requestorEmail,
    }: ApprovalTierChangeEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `[Approved] - Tier Change for - ${customerName}`,
        body: `
                    The following tier change has been approved:
                    
                    Tier Change Details
                    Approver: ${approver}
                    Created by: ${createdBy}
                    Distributor: ${customerName} | ${customerAccount}
        
                    Territory Sales Leader: ${territorySalesLeader}
                    Territory Sales Rep: ${territorySalesRep ?? 'N/A'}

                    Customer Revised Tier Requested: ${revisedTier}
                    Customer Current Tier: ${currentTier}

                    Est. Annual Volume: ${estAnnualVolume}
                    Tier Revision Reason: ${tierRevisionReason}
                    Requestor Comments: ${requestorComments}
                    
                    To view this tier change, click here: ${url}.`,
        // to: ['clay.tiedeman@paragonfilms.com'],
        to: [requestorEmail],
        // to: ['dmendoza@insight2profit.com'],
        cc: [],
    });
}

export function getTierChangeRejectedEmailTemplate(
    {
        customerName,
        approver,
        createdBy,
        customerAccount,
        territorySalesLeader,
        territorySalesRep,
        revisedTier,
        currentTier,
        estAnnualVolume,
        tierRevisionReason,
        requestorComments,
        requestorEmail,
    }: ApprovalTierChangeEmailData,
    url: string
): Email {
    return buildEmail({
        subject: `[Not Approved] - Tier Change for - ${customerName}`,
        body: `
                    The following tier change was not approved, and requires further review:
                    
                    Tier Change Details
                    Approver: ${approver}
                    Created by: ${createdBy}
                    Distributor: ${customerName} | ${customerAccount}
        
                    Territory Sales Leader: ${territorySalesLeader}
                    Territory Sales Rep: ${territorySalesRep ?? 'N/A'}

                    Customer Revised Tier Requested: ${revisedTier}
                    Customer Current Tier: ${currentTier}

                    Est. Annual Volume: ${estAnnualVolume}
                    Tier Revision Reason: ${tierRevisionReason}
                    Requestor Comments: ${requestorComments}
                    
                    To view and update this tier change, click here: ${url}.`,
        // to: ['clay.tiedeman@paragonfilms.com'],
        to: [requestorEmail],
        // to: ['dmendoza@insight2profit.com'],
        cc: [],
    });
}
